import LinearProgress from '@material-ui/core/LinearProgress';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { UiLoader } from 'src/components/UI/loaders';
import { requiredDataHasErrors, requiredDataPercentLoaded } from 'src/store/commonPart/selector';
import { commonSlice } from 'src/store/commonPart/slice';
import { store } from 'src/store/store';
import { getErrorTextFromPreparedApiResponse } from 'src/utils/errors';
import { tryCatch } from 'src/utils/tryCatch';
import { ChildrenPropType } from 'src/utils/types';
import { ErrorPage } from 'src/pages/ErrorPage';

declare global {
  interface Window {
    ONREACTLOAD: (() => void)[]; // the array of function which can be run when React starts
  }
}

type Props = {
  children: ChildrenPropType;
  hasRoutes: boolean;
};

window.ONREACTLOAD = window.ONREACTLOAD || [];

export const UiGlobalLoadingIndicator: React.FC<Props> = ({ children, hasRoutes }: Props) => {
  const MAX_PERCENT = 100;
  const PROGRESS_STEP = 1;
  const PROGRESS_INTERVAL = 500; // ms
  const TIME_TO_SHOW_AFTER_LOAD = 750; // ms

  const timer = useRef<number>(0);

  const [value, setValue] = useState(0);
  const [show, setShow] = useState(true);

  const percent = useSelector(requiredDataPercentLoaded);
  const hasErrors = useSelector(requiredDataHasErrors);

  const readyToProceed = percent >= MAX_PERCENT;

  const errorMessages = useMemo(() => {
    if (hasErrors) {
      window.clearInterval(timer.current);

      return getErrorTextFromPreparedApiResponse(store.getState().common.percentErrors);
    }
    return [];
  }, [hasErrors]);

  const dispatch = useDispatch();

  /** imitate gradual loading */
  useEffect(() => {
    timer.current = window.setInterval(() => {
      setValue((p) => (p >= MAX_PERCENT ? p : p + PROGRESS_STEP));
    }, PROGRESS_INTERVAL);

    return () => {
      window.clearInterval(timer.current);
    };
  }, []);

  /** react to percent's change */
  useEffect(() => {
    setValue(percent);

    if (percent === MAX_PERCENT) {
      window.clearInterval(timer.current);

      window.ONREACTLOAD.forEach((v) => {
        tryCatch(v); // call a function
      });

      window.ONREACTLOAD = [];

      setTimeout(() => {
        setShow(false);
      }, TIME_TO_SHOW_AFTER_LOAD);
    }
  }, [percent]);

  /** calculate if the current page is SPA and can be managed by the app */
  useEffect(() => {
    if (readyToProceed) {
      dispatch(
        commonSlice.actions.setCommon({
          isCurrentPageSPA: hasRoutes,
        })
      );
    }
  }, [dispatch, hasRoutes, readyToProceed]);

  return errorMessages.length ? (
    <ErrorPage message={`Couldn't start the app: "${errorMessages[0]}"`} />
  ) : (
    <>
      {show && <LinearProgress variant="determinate" value={value} className="global-progress" />}

      {hasRoutes &&
        (readyToProceed ? (
          <div id="r-body-wrapper">{children}</div>
        ) : (
          <div className="r-global-block-when-loading">
            <UiLoader />
          </div>
        ))}
    </>
  );
};
